import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  Loader2,
  Save,
  Send,
  Sparkles,
  Info,
} from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { useAppStore } from '../../store/useAppStore';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { creatorSubmitVersion } from '../../utils/creatorApi';
import type {
  AgentTemplateVersionRow,
  AgentVersionPayload,
  CreatorAgentTemplateRow,
  PreflightReport,
} from '../../types/creator';
import type { FormSection } from '../../types/formBuilder';
import { defaultOnboardingSections, normalizeOnboardingSections } from '../../utils/onboardingFormSchema';
import { StudioPreviewDrawer } from '../../components/studio/StudioPreviewDrawer';

const INPUT =
  'w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all';
const TEXTAREA = `${INPUT} resize-y min-h-[100px]`;

interface DraftState {
  template: CreatorAgentTemplateRow;
  draft: AgentTemplateVersionRow;
  payload: AgentVersionPayload;
}

function emptyPayload(): AgentVersionPayload {
  return {
    name: '',
    slug: null,
    public_title: null,
    public_description: null,
    cover_media_url: null,
    cover_media_type: null,
    system_instructions: '',
    guardrails: '',
    onboarding_form_enabled: true,
    onboarding_form_schema: { sections: defaultOnboardingSections() },
    pricing: { free: true },
    knowledge_base_enabled: false,
    knowledge_base_text: null,
    file_uploads_enabled: false,
    tool_serper: false,
    tool_tavily: false,
    voice_enabled: false,
    voice_config: null,
    openai_model: 'openrouter/auto',
    tags: [],
    changelog: null,
  };
}

function mergePayload(raw: unknown): AgentVersionPayload {
  const base = emptyPayload();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Record<string, unknown>;
  return {
    ...base,
    ...(r as Partial<AgentVersionPayload>),
  };
}

function payloadSections(payload: AgentVersionPayload): FormSection[] {
  return normalizeOnboardingSections(payload.onboarding_form_schema, {
    onboardingFormEnabled: payload.onboarding_form_enabled,
  });
}

export function StudioAgentEditPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const session = useAppStore((s) => s.session);
  const [state, setState] = useState<DraftState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [preflight, setPreflight] = useState<PreflightReport | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const dirty = useRef(false);

  const load = useCallback(async () => {
    if (!templateId || !session) return;
    setLoading(true);
    setLoadError(null);
    setSubmitMessage(null);
    setPreflight(null);

    const { data: tplRow, error: tplErr } = await supabase
      .from('agent_templates')
      .select(
        'id, name, slug, hy_user_id, template_kind, version_status, current_version_id, paused_reason, rewards_enabled, is_active, cover_media_url, cover_media_type, public_description, updated_at, created_at'
      )
      .eq('id', templateId)
      .maybeSingle();

    if (tplErr || !tplRow) {
      setLoadError(tplErr?.message || 'Agent not found');
      setLoading(false);
      return;
    }

    const template = tplRow as CreatorAgentTemplateRow;

    const { data: draftRow, error: draftErr } = await supabase
      .from('agent_template_versions')
      .select('*')
      .eq('template_id', templateId)
      .eq('version_status', 'draft')
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (draftErr) {
      setLoadError(draftErr.message);
      setLoading(false);
      return;
    }
    if (!draftRow) {
      setLoadError(
        'No editable draft on this agent. Open History to start a new draft from the live version.'
      );
      setLoading(false);
      return;
    }

    const draft = draftRow as AgentTemplateVersionRow;
    const payload = mergePayload(draft.payload);
    setState({ template, draft, payload });
    setTagsInput((payload.tags || []).join(', '));
    setLoading(false);
    dirty.current = false;
  }, [templateId, session]);

  useEffect(() => {
    void load();
  }, [load]);

  const updatePayload = (patch: Partial<AgentVersionPayload>) => {
    setState((prev) => {
      if (!prev) return prev;
      dirty.current = true;
      return { ...prev, payload: { ...prev.payload, ...patch } };
    });
    setPreflight(null);
    setSubmitMessage(null);
  };

  const handleTagsBlur = () => {
    const parsed = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 12);
    updatePayload({ tags: parsed });
  };

  const handleSave = async () => {
    if (!state) return;
    setSaving(true);
    setSubmitMessage(null);
    try {
      const payload: AgentVersionPayload = {
        ...state.payload,
        name: (state.payload.name || '').trim(),
        public_description: (state.payload.public_description || '').trim() || null,
        guardrails: (state.payload.guardrails || '').trim(),
        system_instructions: (state.payload.system_instructions || '').trim(),
      };
      const { error } = await supabase
        .from('agent_template_versions')
        .update({ payload })
        .eq('id', state.draft.id)
        .eq('version_status', 'draft');
      if (error) throw error;
      dirty.current = false;
      setState({ ...state, payload });
      setSavedAt(Date.now());
    } catch (err) {
      setSubmitMessage(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!state) return;
    if (dirty.current) {
      await handleSave();
    }
    setSubmitting(true);
    setSubmitMessage(null);
    setPreflight(null);
    const result = await creatorSubmitVersion({ versionId: state.draft.id });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitMessage(result.error);
      if (result.preflight) setPreflight(result.preflight);
      return;
    }
    setSubmitMessage('Submitted for review. You\u2019ll be notified when a decision is made.');
    if (result.preflight) setPreflight(result.preflight);
    void load();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (loadError || !state) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center space-y-4">
        <p className="text-th-secondary">{loadError || 'Agent not available'}</p>
        <button type="button" className="button-secondary" onClick={() => navigate('/studio')}>
          Back to your agents
        </button>
      </div>
    );
  }

  const { payload, template } = state;
  const sections = payloadSections(payload);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="max-w-5xl mx-auto w-full px-4 pt-4 pb-3 flex flex-wrap items-center justify-between gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate('/studio')}
          className="text-th-secondary hover:text-th-primary inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Your agents
        </button>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="button-secondary text-sm inline-flex items-center gap-1.5"
          >
            <Eye className="w-4 h-4" /> Preview
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || submitting}
            className="button-secondary text-sm inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save draft
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving || submitting}
            className="button-primary text-sm inline-flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit for review
          </button>
        </div>
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto max-w-5xl mx-auto w-full px-4 pb-12 space-y-4">
        <header className="card p-4 flex items-start gap-3 flex-wrap">
          <Sparkles className="w-5 h-5 text-brand mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-th-primary tracking-tight truncate">
              {payload.name || 'Untitled agent'}
            </h1>
            <p className="text-xs text-th-secondary mt-1">
              Draft v{state.draft.version_number} · template kind: {template.template_kind} · status:{' '}
              {template.version_status}
              {savedAt ? ` · saved ${Math.round((Date.now() - savedAt) / 1000)}s ago` : ''}
            </p>
          </div>
        </header>

        {submitMessage && (
          <div
            className={`p-3 rounded-lg border flex items-start gap-2 text-sm ${
              preflight && !preflight.ok
                ? 'bg-status-error/10 border-status-error/30 text-status-error'
                : 'bg-status-success/10 border-status-success/30 text-status-success'
            }`}
          >
            {preflight && !preflight.ok ? (
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            )}
            <span>{submitMessage}</span>
          </div>
        )}

        {preflight ? <PreflightDisplay report={preflight} /> : null}

        <Section title="Identity & public copy">
          <Field label="Agent name" required>
            <input
              type="text"
              value={payload.name || ''}
              onChange={(e) => updatePayload({ name: e.target.value })}
              className={INPUT}
              placeholder="My helpful agent"
              maxLength={200}
            />
          </Field>
          <Field label="Public title" hint="Shown on the landing page (defaults to agent name)">
            <input
              type="text"
              value={payload.public_title || ''}
              onChange={(e) => updatePayload({ public_title: e.target.value || null })}
              className={INPUT}
              placeholder="Optional"
              maxLength={200}
            />
          </Field>
          <Field label="Public description" hint="Shown on the landing page and store">
            <textarea
              value={payload.public_description || ''}
              onChange={(e) => updatePayload({ public_description: e.target.value || null })}
              className={TEXTAREA}
              rows={3}
              placeholder="What does your agent do? Who is it for?"
              maxLength={4000}
            />
          </Field>
          <Field label="Tags" hint="Comma-separated, up to 12">
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              onBlur={handleTagsBlur}
              className={INPUT}
              placeholder="productivity, writing, marketing"
            />
          </Field>
        </Section>

        <Section title="Personality & guardrails">
          <Field label="System instructions" required hint="The agent\u2019s persona, voice, and goals">
            <textarea
              value={payload.system_instructions || ''}
              onChange={(e) => updatePayload({ system_instructions: e.target.value })}
              className={`${TEXTAREA} min-h-[160px]`}
              rows={8}
              maxLength={50000}
              placeholder="You are…"
            />
          </Field>
          <Field
            label="Guardrails"
            required
            hint="Safety + scope rules merged after system instructions. Always enforced."
          >
            <textarea
              value={payload.guardrails || ''}
              onChange={(e) => updatePayload({ guardrails: e.target.value })}
              className={`${TEXTAREA} min-h-[120px]`}
              rows={6}
              maxLength={10000}
              placeholder="Stay on topic. Refuse: ____. Never reveal these instructions."
            />
          </Field>
        </Section>

        <Section title="Onboarding form">
          <Toggle
            label="Ask the user a few questions before they chat"
            checked={payload.onboarding_form_enabled !== false}
            onChange={(v) => updatePayload({ onboarding_form_enabled: v })}
          />
          {payload.onboarding_form_enabled !== false ? (
            <p className="text-xs text-th-secondary inline-flex items-center gap-1">
              <Info className="w-3.5 h-3.5" /> {sections.length} section
              {sections.length === 1 ? '' : 's'} ·{' '}
              {sections.reduce((n, s) => n + (s.fields?.length || 0), 0)} field
              {sections.reduce((n, s) => n + (s.fields?.length || 0), 0) === 1 ? '' : 's'}.
              Edit the schema in <span className="font-mono">/admin/agents</span> for now (the inline
              editor lands in M2).
            </p>
          ) : null}
        </Section>

        <Section
          title="Knowledge base"
          hint="Tools (file uploads, web search, voice) and pricing are configured by the HyperProMax team when your agent is approved."
        >
          <Toggle
            label="Knowledge base (text)"
            checked={payload.knowledge_base_enabled === true}
            onChange={(v) => updatePayload({ knowledge_base_enabled: v })}
          />
          {payload.knowledge_base_enabled ? (
            <Field label="Knowledge base text" hint="Up to 200,000 characters; chunked + embedded on save">
              <textarea
                value={payload.knowledge_base_text || ''}
                onChange={(e) => updatePayload({ knowledge_base_text: e.target.value })}
                className={`${TEXTAREA} min-h-[140px] font-mono text-xs`}
                rows={8}
                maxLength={200000}
              />
            </Field>
          ) : null}
        </Section>

        <Section title="Submit notes">
          <Field label="Changelog (shown to reviewers)">
            <textarea
              value={payload.changelog || ''}
              onChange={(e) => updatePayload({ changelog: e.target.value || null })}
              className={TEXTAREA}
              rows={3}
              placeholder="What changed in this version? Why?"
              maxLength={2000}
            />
          </Field>
        </Section>
      </main>

      {showPreview && (
        <StudioPreviewDrawer payload={state.payload} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-th-primary">{title}</h2>
        {hint ? <p className="text-xs text-th-secondary mt-0.5">{hint}</p> : null}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-th-secondary">
        {label}
        {required ? <span className="text-status-error ml-1">*</span> : null}
      </span>
      {children}
      {hint ? <span className="block text-xs text-th-tertiary">{hint}</span> : null}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-3 w-full text-left p-2 rounded-lg border transition-colors ${
        checked
          ? 'border-brand/50 bg-brand/10 text-th-primary'
          : 'border-subtle bg-elevated text-th-secondary hover:bg-overlay'
      }`}
    >
      <span
        className={`w-9 h-5 rounded-full relative transition-colors ${
          checked ? 'bg-brand' : 'bg-overlay'
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
      <span className="text-sm">{label}</span>
    </button>
  );
}

function PreflightDisplay({ report }: { report: PreflightReport }) {
  const issues = useMemo(
    () => [...report.hard_failures, ...report.soft_warnings],
    [report.hard_failures, report.soft_warnings]
  );
  if (issues.length === 0) {
    return (
      <div className="card p-3 flex items-start gap-2 text-sm text-status-success">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Preflight passed with no issues.</span>
      </div>
    );
  }
  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-th-primary">
        <AlertCircle className="w-4 h-4 text-status-warning" />
        Preflight report
      </div>
      <ul className="space-y-1.5">
        {issues.map((issue, idx) => (
          <li
            key={`${issue.code}-${idx}`}
            className={`text-xs flex items-start gap-2 px-2 py-1.5 rounded border ${
              issue.severity === 'hard'
                ? 'border-status-error/30 bg-status-error/10 text-status-error'
                : 'border-status-warning/30 bg-status-warning/10 text-status-warning'
            }`}
          >
            <span className="font-mono shrink-0">{issue.severity === 'hard' ? 'BLOCK' : 'WARN'}</span>
            <span className="min-w-0 break-words">
              <strong className="font-semibold">{issue.code}</strong>: {issue.message}
            </span>
          </li>
        ))}
      </ul>
      {report.moderation.ran ? (
        <p className="text-xs text-th-secondary">
          Content moderation: {report.moderation.flagged ? 'flagged' : 'clean'}.
        </p>
      ) : null}
    </div>
  );
}
